import { Tabs as BaseTabs } from '@base-ui/react';
import { Typography } from '@scaleaq/scaleui';
import styled, { css } from 'styled-components';

import { responsive } from '@/utils';

import { tabsColors, tabsInset, tabsLayout } from './tabs.constants.ts';

export type TabsOrientation = 'horizontal' | 'vertical';

const { tabSurface } = tabsColors;

const tabIsSelected = '&[data-selected], &[data-active]';
const tabIsDisabled = '&:disabled, &[data-disabled], &[aria-disabled="true"]';
const tabIsEnabled = ':not(:disabled):not([data-disabled]):not([aria-disabled="true"])';

export const StyledTabsRoot = styled(BaseTabs.Root)<{ $orientation: TabsOrientation }>`
  display: flex;
  align-items: stretch;
  min-height: 10rem;
  height: 100%;
  background: ${tabsColors.rootBackground};

  ${({ $orientation }) =>
    $orientation === 'vertical'
      ? css`
          flex-direction: row;
          flex: 1 1 auto;
          min-height: 0;
          max-height: 100%;
          min-width: 0;
          overflow: hidden;
        `
      : css`
          flex-direction: column;
          min-width: 22rem;
          border-radius: ${responsive.radius.medium};
          overflow: hidden;
        `}
`;

export const StyledTabsRail = styled.aside<{ $orientation: TabsOrientation }>`
  display: flex;
  flex-direction: column;
  gap: ${tabsInset};
  flex-shrink: 0;

  ${({ $orientation }) =>
    $orientation === 'vertical'
      ? css`
          width: ${tabsLayout.verticalRailWidth};
          padding: ${tabsInset};
          border-right: 1px solid ${tabsColors.divider};
          min-height: 0;
          overflow-y: auto;
        `
      : css`
          padding: ${responsive.spacing.large} ${responsive.spacing.large} 0;
        `}
`;

export const StyledTabsHeading = styled(Typography)`
  font-weight: 700;
  padding: 0 ${responsive.spacing.small};
`;

export const StyledTabsList = styled(BaseTabs.List)<{ $orientation: TabsOrientation }>`
  display: flex;

  ${({ $orientation }) =>
    $orientation === 'vertical'
      ? css`
          flex-direction: column;
          width: 100%;
        `
      : css`
          flex-direction: row;
          flex-wrap: wrap;
          padding: ${responsive.spacing.medium} ${responsive.spacing.large};
          border-bottom: 1px solid ${tabsColors.divider};
        `}
`;

/** Mirrors ScaleUI `AppSideBarItem` `itemTriggerBaseStyles`; Base UI uses `data-active` / `data-disabled`. */
export const StyledTabsTab = styled(BaseTabs.Tab)<{ $orientation: TabsOrientation }>`
  text-decoration: none;
  border: none;
  margin: 0;
  user-select: none;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${tabsLayout.tabHeight};
  padding: ${responsive.spacing.medium} ${tabsInset};
  border-radius: ${responsive.radius.medium};
  cursor: pointer;
  font: inherit;
  text-align: left;
  word-break: break-all;
  color: ${tabSurface.default.color};
  background-color: ${tabSurface.default.background};

  &:hover${tabIsEnabled} {
    color: ${tabSurface.hover.color};
    background-color: ${tabSurface.hover.background};
  }

  &:active${tabIsEnabled}, ${tabIsSelected}${tabIsEnabled} {
    color: ${tabSurface.selected.color};
    background-color: ${tabSurface.selected.background};
  }

  ${tabIsSelected}${tabIsEnabled}:hover {
    cursor: pointer;
    color: ${tabSurface.selected.hover.color};
    background-color: ${tabSurface.selected.hover.background};
  }

  ${tabIsDisabled},
  ${tabIsDisabled}${tabIsSelected} {
    cursor: not-allowed;
    color: ${tabSurface.disabled.color};
    background-color: ${tabSurface.disabled.background};

    &:hover {
      color: ${tabSurface.disabled.color};
      background-color: ${tabSurface.disabled.background};
    }
  }

  &:focus-visible {
    outline: 2px solid ${tabsColors.tabFocusOutline};
    outline-offset: -0.125rem;
  }

  ${({ $orientation }) =>
    $orientation === 'horizontal' &&
    css`
      width: auto;
      text-align: center;
      white-space: nowrap;
    `}
`;

export const StyledTabsPanel = styled(BaseTabs.Panel)<{ $orientation: TabsOrientation }>`
  flex: 1;
  min-height: 0;
  min-width: 0;

  ${({ $orientation }) =>
    $orientation === 'vertical' &&
    css`
      min-width: 12rem;
      overflow-y: auto;
    `}

  &[data-hidden] {
    display: none;
  }
`;
